// Israel cities dataset (curated, by population) with coordinates for radius matching.
// Coords are approximate city centers — accurate enough for a 40km service-radius filter.
// For any city NOT here, the app falls back to the /api/geocode route (Google Maps).

import { resolveCity, haversineKm as geoHaversineKm } from './geo';

export type City = { name: string; lat: number; lng: number; pop: number };

// Sorted roughly by population (largest first).
export const ISRAEL_CITIES: City[] = [
  { name: 'ירושלים', lat: 31.78, lng: 35.22, pop: 936000 },
  { name: 'תל אביב', lat: 32.08, lng: 34.78, pop: 460000 },
  { name: 'חיפה', lat: 32.79, lng: 34.99, pop: 285000 },
  { name: 'ראשון לציון', lat: 31.97, lng: 34.79, pop: 254000 },
  { name: 'פתח תקווה', lat: 32.09, lng: 34.89, pop: 248000 },
  { name: 'אשדוד', lat: 31.79, lng: 34.65, pop: 225000 },
  { name: 'נתניה', lat: 32.33, lng: 34.86, pop: 221000 },
  { name: 'באר שבע', lat: 31.25, lng: 34.79, pop: 209000 },
  { name: 'בני ברק', lat: 32.08, lng: 34.83, pop: 204000 },
  { name: 'חולון', lat: 32.01, lng: 34.77, pop: 188000 },
  { name: 'רמת גן', lat: 32.07, lng: 34.82, pop: 170000 },
  { name: 'אשקלון', lat: 31.67, lng: 34.57, pop: 145000 },
  { name: 'רחובות', lat: 31.89, lng: 34.81, pop: 143000 },
  { name: 'בת ים', lat: 32.02, lng: 34.75, pop: 128000 },
  { name: 'בית שמש', lat: 31.75, lng: 34.99, pop: 124000 },
  { name: 'כפר סבא', lat: 32.18, lng: 34.91, pop: 101000 },
  { name: 'הרצליה', lat: 32.16, lng: 34.84, pop: 97000 },
  { name: 'חדרה', lat: 32.43, lng: 34.92, pop: 97000 },
  { name: 'מודיעין', lat: 31.89, lng: 35.01, pop: 93000 },
  { name: 'מודיעין עילית', lat: 31.93, lng: 35.04, pop: 80000 },
  { name: 'נצרת', lat: 32.70, lng: 35.30, pop: 78000 },
  { name: 'לוד', lat: 31.95, lng: 34.89, pop: 78000 },
  { name: 'רמלה', lat: 31.93, lng: 34.87, pop: 77000 },
  { name: 'רעננה', lat: 32.18, lng: 34.87, pop: 75000 },
  { name: 'ראש העין', lat: 32.10, lng: 34.95, pop: 75000 },
  { name: 'רהט', lat: 31.39, lng: 34.75, pop: 75000 },
  { name: 'הוד השרון', lat: 32.15, lng: 34.89, pop: 65000 },
  { name: 'ביתר עילית', lat: 31.70, lng: 35.12, pop: 64000 },
  { name: 'גבעתיים', lat: 32.07, lng: 34.81, pop: 60000 },
  { name: 'קריית אתא', lat: 32.81, lng: 35.10, pop: 58000 },
  { name: 'קריית גת', lat: 31.61, lng: 34.77, pop: 58000 },
  // Colloquial cluster name for the Kiryat Motzkin/Bialik/Yam/Ata area north of Haifa —
  // not a real municipality, but common in search queries and clusterads city plans.
  { name: 'קריות', lat: 32.83, lng: 35.08, pop: 120000 },
  { name: 'נהריה', lat: 33.01, lng: 35.09, pop: 58000 },
  { name: 'אום אל-פחם', lat: 32.52, lng: 35.15, pop: 56000 },
  { name: 'עפולה', lat: 32.61, lng: 35.29, pop: 56000 },
  { name: 'אילת', lat: 29.56, lng: 34.95, pop: 52000 },
  { name: 'נס ציונה', lat: 31.93, lng: 34.80, pop: 51000 },
  { name: 'יבנה', lat: 31.88, lng: 34.74, pop: 51000 },
  { name: 'אלעד', lat: 32.05, lng: 34.95, pop: 50000 },
  { name: 'עכו', lat: 32.93, lng: 35.08, pop: 49000 },
  { name: 'טבריה', lat: 32.79, lng: 35.53, pop: 48000 },
  { name: 'רמת השרון', lat: 32.15, lng: 34.84, pop: 47000 },
  { name: 'כרמיאל', lat: 32.92, lng: 35.30, pop: 46000 },
  { name: 'פרדס חנה-כרכור', lat: 32.47, lng: 34.97, pop: 45000 },
  { name: 'טייבה', lat: 32.27, lng: 35.01, pop: 44000 },
  { name: 'נתיבות', lat: 31.42, lng: 34.59, pop: 43000 },
  { name: 'שפרעם', lat: 32.81, lng: 35.17, pop: 42000 },
  { name: 'קריית מוצקין', lat: 32.83, lng: 35.08, pop: 41000 },
  { name: 'נוף הגליל', lat: 32.71, lng: 35.32, pop: 41000 },
  { name: 'קריית ביאליק', lat: 32.83, lng: 35.09, pop: 40000 },
  { name: 'קריית ים', lat: 32.85, lng: 35.07, pop: 40000 },
  { name: 'קריית אונו', lat: 32.06, lng: 34.86, pop: 39000 },
  { name: 'מעלה אדומים', lat: 31.78, lng: 35.30, pop: 38000 },
  { name: 'אור יהודה', lat: 32.03, lng: 34.85, pop: 37000 },
  { name: 'צפת', lat: 32.96, lng: 35.50, pop: 37000 },
  { name: 'אופקים', lat: 31.31, lng: 34.62, pop: 36000 },
  { name: 'תמרה', lat: 32.85, lng: 35.20, pop: 35000 },
  { name: 'דימונה', lat: 31.07, lng: 35.03, pop: 34000 },
  { name: 'טירת כרמל', lat: 32.76, lng: 34.97, pop: 27000 },
  { name: 'טירה', lat: 32.23, lng: 34.95, pop: 27000 },
  { name: 'סח׳נין', lat: 32.86, lng: 35.30, pop: 31000 },
  { name: 'גדרה', lat: 31.81, lng: 34.78, pop: 30000 },
  { name: 'באקה אל-גרבייה', lat: 32.42, lng: 35.04, pop: 30000 },
  { name: 'יהוד', lat: 32.03, lng: 34.89, pop: 30000 },
  { name: 'שדרות', lat: 31.52, lng: 34.60, pop: 31000 },
  { name: 'מגדל העמק', lat: 32.67, lng: 35.24, pop: 26000 },
  { name: 'נשר', lat: 32.77, lng: 35.04, pop: 25000 },
  { name: 'גן יבנה', lat: 31.79, lng: 34.70, pop: 25000 },
  { name: 'יוקנעם', lat: 32.66, lng: 35.10, pop: 25000 },
  { name: 'כפר יונה', lat: 32.32, lng: 34.93, pop: 25000 },
  { name: 'קריית שמונה', lat: 33.21, lng: 35.57, pop: 24000 },
  { name: 'זכרון יעקב', lat: 32.57, lng: 34.95, pop: 24000 },
  { name: 'קלנסווה', lat: 32.28, lng: 34.98, pop: 24000 },
  { name: 'כפר קאסם', lat: 32.11, lng: 34.98, pop: 24000 },
  { name: 'אריאל', lat: 32.10, lng: 35.18, pop: 21000 },
  { name: 'שוהם', lat: 31.99, lng: 34.95, pop: 21000 },
  { name: 'גבעת שמואל', lat: 32.08, lng: 34.85, pop: 28000 },
  { name: 'ערד', lat: 31.26, lng: 35.21, pop: 28000 },
  { name: 'מעלות-תרשיחא', lat: 33.02, lng: 35.27, pop: 22000 },
  { name: 'בית שאן', lat: 32.50, lng: 35.50, pop: 18000 },
  { name: 'אור עקיבא', lat: 32.51, lng: 34.92, pop: 18000 },
  { name: 'גבעת זאב', lat: 31.86, lng: 35.17, pop: 18000 },
  { name: 'דאלית אל-כרמל', lat: 32.69, lng: 35.05, pop: 17000 },
  { name: 'קריית מלאכי', lat: 31.73, lng: 34.74, pop: 24000 },
  { name: 'מזכרת בתיה', lat: 31.85, lng: 34.84, pop: 14000 },
  { name: 'קריית עקרון', lat: 31.86, lng: 34.82, pop: 13000 },
  { name: 'תל מונד', lat: 32.25, lng: 34.92, pop: 14000 },
  { name: 'אבן יהודה', lat: 32.27, lng: 34.89, pop: 14000 },
  { name: 'קדימה-צורן', lat: 32.27, lng: 34.92, pop: 23000 },
  { name: 'בנימינה-גבעת עדה', lat: 32.51, lng: 34.95, pop: 17000 },
  { name: 'יבנאל', lat: 32.71, lng: 35.50, pop: 4000 },
  { name: 'מיתר', lat: 31.32, lng: 34.93, pop: 8000 },
  { name: 'להבים', lat: 31.37, lng: 34.81, pop: 7000 },
  { name: 'עומר', lat: 31.26, lng: 34.85, pop: 8000 },
  { name: 'כפר ורדים', lat: 32.99, lng: 35.27, pop: 6000 },
  { name: 'מצפה רמון', lat: 30.61, lng: 34.80, pop: 5000 },
  { name: 'ירוחם', lat: 30.99, lng: 34.93, pop: 12000 },
  { name: 'קצרין', lat: 32.99, lng: 35.69, pop: 8000 },
  { name: 'מטולה', lat: 33.28, lng: 35.58, pop: 2000 },
  { name: 'כפר כנא', lat: 32.75, lng: 35.34, pop: 23000 },
  { name: 'מגאר', lat: 32.89, lng: 35.41, pop: 22000 },
  { name: 'יקנעם עילית', lat: 32.66, lng: 35.11, pop: 25000 },
  { name: 'פרדסיה', lat: 32.31, lng: 34.91, pop: 7000 },
  { name: 'גני תקווה', lat: 32.06, lng: 34.87, pop: 22000 },
  { name: 'סביון', lat: 32.05, lng: 34.88, pop: 4000 },
  { name: 'אזור', lat: 32.02, lng: 34.80, pop: 13000 },
  { name: 'כפר שמריהו', lat: 32.19, lng: 34.82, pop: 2000 },
  { name: 'נווה ימין', lat: 32.16, lng: 34.93, pop: 3000 },
  { name: 'ראש פינה', lat: 32.97, lng: 35.54, pop: 3000 },
  { name: 'עתלית', lat: 32.69, lng: 34.94, pop: 7000 },
  { name: 'פוריידיס', lat: 32.60, lng: 34.95, pop: 13000 },
  { name: 'ג׳סר א-זרקא', lat: 32.54, lng: 34.91, pop: 15000 },
  { name: 'אבו גוש', lat: 31.81, lng: 35.11, pop: 8000 },
  { name: 'מבשרת ציון', lat: 31.80, lng: 35.15, pop: 25000 },
  { name: 'הרצליה פיתוח', lat: 32.17, lng: 34.81, pop: 5000 },
];
// 2026-09-30 (Ramla→Modiin incident): the 2-decimal points above (±0.7 km, Haifa 2.4 km off) are replaced at load by the
// VERIFIED CBS coordinates (lib/geo.ts) — the names/populations here stay the site's curated list (URL slugs, chips).
for (const c of ISRAEL_CITIES) { const g = resolveCity(c.name); if (g) { c.lat = g.lat; c.lng = g.lng; } }

const NORMALIZE = (s: string) =>
  (s || '')
    .replace(/,?\s*ישראל\s*$/i, '')
    .split(',')[0]
    .replace(/תל[\s\-־]+אביב[\s\-־]+יפו/g, 'תל אביב')
    .replace(/[\-־]+/g, ' ') // 2026-09-22: 'גן-יבנה' (slug form) must resolve like 'גן יבנה'
    .replace(/[״"׳']/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const BY_NAME: Record<string, City> = {};
for (const c of ISRAEL_CITIES) BY_NAME[NORMALIZE(c.name)] = c;

/**
 * SYSTEM RULE (2026-07-29, user directive): this is the ONE canonical source for any
 * Israeli city list shown to a visitor — questionnaire city chips, AMP city steps,
 * autocomplete, anywhere. Always population-descending (largest city first). Every
 * engine must call this function directly; never hand-write/copy-paste a Hebrew city
 * array elsewhere — that's exactly how app/api/cities/route.ts and
 * scripts/seo/build-service-clusters.mjs drifted out of order (Tel Aviv before
 * Jerusalem) before this fix. QuestionnaireWizard.tsx enforces this at runtime too: any
 * question with outputKey==='city' has its stored `options` ignored entirely and
 * replaced with a fresh popularCities() call (see `isCity`/`cityChips` there) — so even
 * a stale/wrong city list saved in the DB can never actually reach a visitor.
 */
export function popularCities(n = 8): string[] {
  return [...ISRAEL_CITIES].sort((a, b) => b.pop - a.pop).slice(0, n).map(c => c.name);
}

/** Resolve a city name to coordinates from the local dataset (exact or contained match). */
export function findCity(name?: string | null): City | null {
  const q = NORMALIZE(name || '');
  if (!q) return null;
  if (BY_NAME[q]) return BY_NAME[q];
  // 2026-09-30: every other name goes through THE geo engine (lib/geo.ts — CBS localities + aliases: קרית/קריית, מודיעין
  // מכבים רעות, יהוד מונוסון, ת"א…). The old loose substring match is GONE — it turned "גן יבנה" into יבנה, "אור יהודה" into
  // יהוד and left "קרית מוצקין" unresolved. A locality that IS one of our curated cities returns the curated City (same name
  // → same URL slug); any other locality returns itself with its verified point.
  const g = resolveCity(name);
  if (!g) return null;
  return CURATED_BY_CODE.get(g.code) ?? { name: g.name, lat: g.lat, lng: g.lng, pop: g.pop };
}
const CURATED_BY_CODE = new Map<number, City>();
for (const c of ISRAEL_CITIES) { const g = resolveCity(c.name); if (g?.code && !CURATED_BY_CODE.has(g.code)) CURATED_BY_CODE.set(g.code, c); }

/** Live substring search across the full dataset (questionnaire "type to search" city UI,
 *  2026-08-23) — prefix matches rank above mid-string matches, each group sorted by
 *  population descending, so typing "רמת" surfaces רמת גן/רמת השרון before a random
 *  contains-match further down the list. */
export function searchCities(query: string, limit = 20): string[] {
  const q = NORMALIZE(query);
  if (!q) return [];
  const starts: City[] = [];
  const contains: City[] = [];
  for (const c of ISRAEL_CITIES) {
    const n = NORMALIZE(c.name);
    if (n.startsWith(q)) starts.push(c);
    else if (n.includes(q)) contains.push(c);
  }
  const byPop = (a: City, b: City) => b.pop - a.pop;
  return [...starts.sort(byPop), ...contains.sort(byPop)].slice(0, limit).map(c => c.name);
}

/** Finds a city NAMED inside free text (WhatsApp replies such as "בפתח תקווה, בדיקת קרקע"; Founder 2026-09-27):
 *  exact n-gram match (1-3 words, leading ב/מ/ל/ה prepositions stripped) wins, longest first; then a contained
 *  dataset name of ≥4 normalized letters, longest first (so "רמת השרון" beats "רמת"). Returns the city and the rest
 *  of the text (the customer's need) with the city removed. */
export function detectCityInText(text?: string | null): { city: City; rest: string } | null {
  const raw = String(text || '');
  const tokens = raw.split(/[^\u0590-\u05FFA-Za-z'"״׳-]+/).filter(Boolean);
  const strip = (w: string) => w.replace(/^(?:וב|ומ|ול|ב|מ|ל)/, ''); // never ה — "המודיעין" is not מודיעין (bughunter)
  const ABBR: Record<string, string> = { 'ראשלצ': 'ראשון לציון', 'תא': 'תל אביב', 'פת': 'פתח תקווה', 'כפס': 'כפר סבא', 'בש': 'באר שבע', 'קג': 'קריית גת', 'רג': 'רמת גן', 'ראשון': 'ראשון לציון', 'ירושלים': 'ירושלים', 'telaviv': 'תל אביב', 'jerusalem': 'ירושלים', 'haifa': 'חיפה', 'מודיעיןמכביםרעות': 'מודיעין', 'מודיעיןעיליתמכביםרעות': 'מודיעין' };
  const canon = (c: string) => { const fix = (x: string) => x.replace(/[-־]+/g, ' ').replace(/\s+/g, ' ').replace(/(^|\s)קרית(?=\s|$)/g, '$1קריית').replace(/תקוה(?=\s|$)/g, 'תקווה'); /* hyphens = spaces: מודיעין-מכבים-רעות (bughunter 2026-09-28) */ const n = fix(NORMALIZE(c)).replace(/\s/g, ''); const a = ABBR[n] || ABBR[n.toLowerCase()]; return a ? NORMALIZE(a) : fix(NORMALIZE(c)); };
  let best: { city: City; span: string } | null = null;
  const meaningful = tokens.filter(t => !TEXT_FILLER.has(t)).length;
  for (let i = 0; i < tokens.length; i++) for (let n = 3; n >= 1; n--) {
    if (i + n > tokens.length) continue;
    const words = tokens.slice(i, i + n);
    for (const cand of [words.join(' '), [strip(words[0]), ...words.slice(1)].join(' ')]) {
      // 22/10/26 (ticket 14259 "באר יעקב" not understood): the curated list first, then EVERY CBS locality (lib/geo.ts) —
      // guarded so a common word / first name inside a sentence never becomes a town (see localityInText)
      const hit = BY_NAME[canon(cand)] || localityInText(cand, n, meaningful === n);
      if (hit && (!best || cand.length > best.span.length)) best = { city: hit, span: words.join(' ') };
    }
  }
  // no substring fallback: "ראש הממשלה נתניהו" must not become נתניה (bughunter 2026-09-27) — whole tokens only
  if (!best) return null;
  // "באזור המרכז / הצפון / השרון" = "in the … area", not the town Azor (bughunter 2026-09-28)
  if (best.city.name === 'אזור' && /(?:^|\s)ב?אזור\s+ה[\u0590-\u05FF]/.test(raw)) return null;
  if (best.city.name === 'עומר' && /ל["״׳']?ג\s*ב?עומר/.test(raw)) return null; // ל"ג בעומר is a holiday, not the town (22/10/26 sim)
  const rest = raw.replace(best.span, ' ').replace(/(^|\s)(?:וב|ומ|ול|ב|מ|ל|ה)(?=\s)/g, ' ').replace(/^[\s,.;:\-–|]+|[\s,.;:\-–|]+$/g, '').replace(/\s+/g, ' ').replace(/\s+([,.;:])/g, '$1');
  return { city: best.city, rest };
}

// Words that may surround a bare city answer ("אני גר בבאר יעקב", "העיר: שוהם") — the answer still counts as "only a city".
const TEXT_FILLER = new Set(['אני', 'גר', 'גרה', 'גרים', 'גרות', 'עיר', 'העיר', 'בעיר', 'זה', 'זו', 'היא', 'הוא', 'כן', 'תודה', 'שלום', 'אנחנו', 'מ', 'ב', 'יישוב', 'ביישוב', 'מושב', 'במושב', 'קיבוץ', 'בקיבוץ', 'העבודה', 'עבודה', 'נמצא', 'נמצאת', 'נמצאים']);
// Single-word CBS towns that are also first names / everyday words — named alone they count, inside a sentence they do not.
const LOCALITY_WORDS = new Set(['עומר', 'אפרת', 'שלומי', 'מיתר', 'זמר', 'שילה', 'עמנואל', 'ראמה', 'להבים', 'אורנית', 'טלמון', 'ברכה', 'מגדל', 'מתן', 'ניצן', 'עדי', 'מאור', 'חירות', 'נווה', 'שורש', 'ענב', 'שקד', 'דפנה', 'אורה', 'יקיר', 'חבר']);
/** A CBS locality (any town/moshav/kibbutz, lib/geo.ts) named by `cand` inside free text. Multi-word names ("באר יעקב",
 *  "גן יבנה", "כפר יונה") always count; a single word counts when it is the whole answer, or when it is a real town
 *  (≥5,000 people) that is not also a common word/name. Our curated city wins when it is the same locality. */
function localityInText(cand: string, words: number, wholeAnswer: boolean): City | null {
  const g = resolveCity(cand);
  if (!g || !g.code) return null;
  const cur = CURATED_BY_CODE.get(g.code); if (cur) return cur;
  if (words === 1 && !wholeAnswer && (g.pop < 5000 || LOCALITY_WORDS.has(g.name))) return null;
  return { name: g.name, lat: g.lat, lng: g.lng, pop: g.pop };
}

/** Great-circle distance in km between two lat/lng points. */
export const haversineKm = geoHaversineKm; // THE implementation lives in lib/geo.ts

// 2026-08-19 (user directive): was 40 — "professionals over 25km should be hidden"
// (caught live on /אינסטלטור/אשדוד). "Close enough, no gap" radius, used both for the
// visible listing/lead-routing radius ladder (lib/proMatching.ts) and the SEO-index-
// eligibility check (lib/categoryData.ts's prosInRadius). Beyond this, a match still
// happens (never show zero) but gets flagged+logged as a real demand gap instead of
// silently counting as "close enough".
export const DEFAULT_RADIUS_KM = 25;

/** URL-slug form of a city name — plain space→hyphen, gershayim stripped via NORMALIZE.
 *  Matches the format already used by the clusterads Google Ads pipeline's Final URLs. */
export function citySlug(name: string): string {
  return NORMALIZE(name).replace(/\s+/g, '-');
}

const BY_SLUG: Record<string, City> = {};
for (const c of ISRAEL_CITIES) BY_SLUG[citySlug(c.name)] = c;

/** Resolve a URL city-slug segment back to a City (exact match only — no fuzzy fallback,
 *  unlike findCity, since this drives routing and must not silently match the wrong city). */
export function citySlugToCity(slug: string): City | null {
  return BY_SLUG[slug] || null;
}
